import axios from 'axios';
import { baseApiUrl } from '@/global';

// Campos do contrato exibidos como Sim/Não
const CONTRACT_HISTORY_BOOLEAN_FIELDS = [
  'count_enterprise',
  'ignore_number_maintenance',
  'group_invoice_by_channel',
  'send_invoice_csv',
];

// Campos cujo nome vem das mesmas APIs usadas nos combos do contrato
const CONTRACT_HISTORY_LOOKUP_FIELDS = {
  product_id: 'products',
  plan_type_id: 'planTypes',
  change_reason_group_id: 'reasonGroups',
};

const CONTRACT_HISTORY_HIDDEN_FIELDS = ['contract_id', 'type_id', 'id', 'service_id'];

export const contractHistoryMixin = {
  data() {
    return {
      historyLookups: { products: {}, planTypes: {}, reasonGroups: {} },
    };
  },
  methods: {
    loadHistoryLookups() {
      const toMap = (list, labelKey) =>
        (list || []).reduce((map, item) => ({ ...map, [item.id]: item[labelKey] }), {});

      Promise.all([
        axios(`${baseApiUrl}/v1/product/all`),
        axios(`${baseApiUrl}/v1/plan-types/all`),
        axios(`${baseApiUrl}/v1/change-reason-groups/all`),
      ]).then(([products, planTypes, reasonGroups]) => {
        this.historyLookups = {
          products: toMap(products.data, 'name'),
          planTypes: toMap(planTypes.data, 'description'),
          reasonGroups: toMap(reasonGroups.data, 'description'),
        };
      });
    },
    /**
     * Junta as alterações do grupo por registro + campo (valor antigo da
     * primeira, valor novo da última) e esconde o que não mudou
     */
    buildHistoryRows(audits) {
      const rowsByKey = new Map();

      [...(audits || [])]
        .sort((a, b) => a.id - b.id)
        .forEach((audit) => {
          const list = (audit.event == 'deleted' ? audit.old_values : audit.new_values) || {};
          Object.keys(list).forEach((index) => {
            if (CONTRACT_HISTORY_HIDDEN_FIELDS.includes(index)) {
              return;
            }
            const key = `${audit.auditable_type}-${audit.auditable_id}-${index}`;
            const newRaw = audit.event == 'deleted' ? undefined : list[index];

            if (rowsByKey.has(key)) {
              const row = rowsByKey.get(key);
              row.newRaw = newRaw;
              row.auditId = audit.id;
              return;
            }
            rowsByKey.set(key, {
              key,
              index,
              type: audit.type,
              auditId: audit.id,
              oldRaw: audit.old_values ? audit.old_values[index] : undefined,
              newRaw,
            });
          });
        });

      return [...rowsByKey.values()]
        .map((row) => ({
          key: row.key,
          auditId: row.auditId,
          label: this.getFieldLabel(row.index, row.type),
          // Motivo antigo não é exibido (só o informado neste salvar)
          oldValue: row.index == 'change_reason' ? '-' : this.getValue(row.oldRaw, row.index),
          newValue: this.getValue(row.newRaw, row.index),
        }))
        .filter((row) => row.oldValue !== row.newValue);
    },
    getFieldLabel(index, type) {
      const defaultFields = {
        product_id: 'Produto',
        customer_id: 'Cliente',
        plan_id: 'Plano cadastrado',
        finish: 'Fim do contrato',
        start: 'Início do contrato',
        note: 'Observações',
        billing_method: 'Cobrar por',
        due_day: 'Dia vencimento',
        contract_type: 'Tipo contrato',
        due_in_days: 'Prazo de vencimento',
        price: 'Preço',
        amount: 'Quantidade',
        datas: 'Faixas, Valor mínimo, etc',
        change_reason: 'Motivo da alteração',
        change_reason_group_id: 'Grupo (Motivo da alteração)',
        hired_by: 'Contratado por',
        grace_date: 'Data de carência',
        note_chatbot: 'Informações chatbot',
        status: 'Status',
        plan_type_id: 'Plano',
        readjustment_index_id: 'Índice de reajuste',
        actual_contract_date: 'Data atual do contrato',
        count_enterprise: 'Contabilizar Enterprise',
        ignore_number_maintenance: 'Desconsiderar manutenção de número',
        group_invoice_by_channel: 'Agrupar detalhes da fatura por canal',
        send_invoice_csv: 'Enviar CSV da fatura por e-mail',
      };

      return (defaultFields[index] || index) + (type ? ` - ${type}` : '');
    },
    getValue(value, index) {
      if (
        typeof value !== 'undefined' &&
        value != '0000-00-00' &&
        (index == 'finish' || index == 'start' || index == 'grace_date' || index == 'actual_contract_date')
      ) {
        return value ? this.$options.filters.date(value) : '-';
      }

      if (typeof value !== 'undefined' && index == 'price') {
        return this.$options.filters.money(value ? value : 0);
      }

      if (typeof value !== 'undefined' && index == 'contract_type') {
        return value == 1 ? 'Legado' : value == 2 ? 'TC' : 'PME';
      }

      if (typeof value !== 'undefined' && index == 'billing_method') {
        return value == 1 ? 'Carteira' : 'CNPJ';
      }

      if (typeof value !== 'undefined' && index == 'hired_by') {
        return value == 1 ? 'Termo' : 'Contrato';
      }

      if (typeof value !== 'undefined' && index == 'change_reason') {
        // Caso change reason for igual ao anterior
        return value.replace(/⠀/g, '') || '-';
      }

      if (value && index == 'datas') {
        value = JSON.parse(value);
        if (typeof value === 'object' && value !== null) {
          value = JSON.stringify(value).replace(/\s+/g, '');
        }

        value = value.replace('"billing_type":1', 'Faixa Simples');
        value = value.replace('"billing_type":2', 'Faixa Acumulada');
        value = value.replace('"billing_type":3', 'Faixa Modular');
        value = value.replace('"billing_type":4', 'Faixa Modular Mista');
        value = value.replace('"charge_use_only":1', 'Cobrar todos usuários');
        value = value.replace('"charge_use_only":0', 'Cobrar apenas usuários utilizados');
        value = value.replace('"minimal"', 'Valor mínimo');
        value = value.replace('"extra"', 'Valor extra');
        value = value.replace('"fixed"', 'Valor fixo');
        value = value.replace('"hired_by":0', 'Contrato');
        value = value.replace('"hired_by":1', 'Termo');
        value = value.replace('"contact_recep_total"', 'Contato Receptivo');
        value = value.replace('"receptive_excess"', 'Receptivos por excedentes');
        value = value.replace('"licenses_included"', 'Licenças inclusas');

        const sessionLabels = {
          enterprise_use_facebook_bonus: 'Aplicar bônus cedido pelo facebook',
          enterprise_use_facebook_prices: 'Usar valores do facebook',
          enterprise_use_unified_billing: 'Usar cobrança unificada',
          quantity_unified_billing: 'Sessão Unificada - Quantidade',
          enterprise_session_unified: 'Sessão Unificada - Valor',
          quantity_enterprise_session_active: 'Sessão Ativo - Quantidade',
          enterprise_session_active: 'Sessão Ativo - Valor',
          quantity_enterprise_session_receptive: 'Sessão Receptivo - Quantidade',
          enterprise_session_receptive: 'Sessão Receptivo - Valor',
          quantity_enterprise_session_authentication: 'Sessão Autenticação - Quantidade',
          enterprise_session_authentication: 'Sessão Autenticação - Valor',
          quantity_enterprise_session_utility: 'Sessão Utilitário - Quantidade',
          enterprise_session_utility: 'Sessão Utilitário - Valor',
          quantity_enterprise_session_marketing: 'Sessão Marketing - Quantidade',
          enterprise_session_marketing: 'Sessão Marketing - Valor',
          quantity_enterprise_session_services: 'Sessão Serviço - Quantidade',
          enterprise_session_services: 'Sessão Serviço - Valor',
          number_maintenance: 'Valor manutenção número',
        };
        Object.entries(sessionLabels).forEach(([key, label]) => {
          value = value.split(`"${key}"`).join(label);
        });
      }

      // Caso change reason for igual ao anterior

      if (index == 'status') {
        switch (value != null && value.toString()) {
          case '2':
            return 'Ativo';
          case '1':
            return 'Bloqueado';
          case '0':
            return 'Suspenso';
          default:
        }
      }

      if (typeof value !== 'undefined' && value !== null && CONTRACT_HISTORY_BOOLEAN_FIELDS.includes(index)) {
        return value == 1 ? 'Sim' : 'Não';
      }

      const lookup = CONTRACT_HISTORY_LOOKUP_FIELDS[index];
      if (lookup && value !== null && typeof value !== 'undefined' && value !== '') {
        return this.historyLookups[lookup][value] || value;
      }

      if (index == 'readjustment_index_id') {
        switch (value && value.toString()) {
          case '1':
            return 'Outros';
          case '2':
            return 'IGPM';
          case '3':
            return 'IPCA';
          default:
        }
      }

      if (typeof value === 'string' && value.startsWith('{') && value.endsWith('}')) {
        value = value.substring(1, value.length - 1);
        value = value.replace(/,/g, ', ');
      }

      return value || '-';
    },
  },
};
